import { useState, useCallback } from 'react';
import { getAuditLogs } from '../services/api';
import { AuditRecord } from '../types';
import AuditTable from '../components/AuditTable';

/**
 * Audit log viewer — restricted to reviewer role (FR-007).
 * Supports date range filtering and pagination.
 */
export default function AuditPage() {
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);

  const fetchLogs = useCallback(async (token?: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAuditLogs({
        startDate,
        endDate,
        nextToken: token,
      });
      if (token) {
        setRecords(prev => [...prev, ...response.records]);
      } else {
        setRecords(response.records);
      }
      setNextToken(response.nextToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const handleSearch = useCallback(() => {
    setNextToken(null);
    fetchLogs();
  }, [fetchLogs]);

  const handleLoadMore = useCallback(() => {
    if (nextToken) fetchLogs(nextToken);
  }, [nextToken, fetchLogs]);

  return (
    <div className="audit-page">
      <h2>Audit Log</h2>
      <div className="audit-filters">
        <label>
          From: <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label>
          To: <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
        <button onClick={handleSearch} disabled={loading}>
          {loading ? 'Loading...' : 'Search'}
        </button>
      </div>
      {error && <div className="audit-error">{error}</div>}
      <AuditTable records={records} />
      {nextToken && (
        <button onClick={handleLoadMore} disabled={loading} className="load-more-btn">
          Load More
        </button>
      )}
    </div>
  );
}
